import { Check, ChevronDown } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import { Menu } from 'common-src/components/base';

import { formatDue } from '../../helpers';
import { Badge } from '../Badge';
import { SmallCaps } from '../Visit';
import styles from './MemberRecord.module.scss';

const MemberOption = ({ member, current }) => {
  const due = formatDue(member.mostUrgent.due);
  const dueTone =
    member.tier === 0 ? styles.switchDueUrgent : member.tier === 1 ? styles.switchDueCritical : '';
  return (
    <span className={`${styles.switchOption} ${current ? styles.switchOptionCurrent : ''}`}>
      <span className={styles.switchCheck}>
        {current && <Check size={14} aria-hidden="true" />}
      </span>
      <span className={styles.switchBody}>
        <span className={styles.switchTop}>
          <span className={styles.switchName}>{member.patient?.name}</span>
          <span className={styles.switchMrn}>{member.patient?.mrn}</span>
          <span className={styles.flex} />
          <span className={`${styles.switchDue} ${dueTone}`}>{due.text}</span>
        </span>
        <span className={styles.switchBottom}>
          {member.mostUrgent.priority === 'critical' && <Badge variant="critical">CRITICAL</Badge>}
          <span className={styles.switchTask}>{member.mostUrgent.title}</span>
          <span className={styles.switchCount}>{member.openCount} open</span>
        </span>
      </span>
    </span>
  );
};

const MemberSwitcher = ({ name, patientId, members, onSelect }) => {
  const heading = {
    title: 'heading',
    value: 'heading',
    label: (
      <SmallCaps as="div" className={styles.switchHeading}>
        Your members · {members.length}
        <span className={styles.switchHeadingHint}>
          open tasks assigned to you, most urgent first
        </span>
      </SmallCaps>
    ),
  };
  const rows = members.map((member) => ({
    title: member.patientId,
    value: member.patientId,
    label: <MemberOption member={member} current={member.patientId === `${patientId}`} />,
    onClick: () => onSelect(member.patientId),
  }));

  const viewing = members.some((member) => member.patientId === `${patientId}`)
    ? []
    : [
        {
          title: 'viewing-heading',
          value: 'viewing-heading',
          label: (
            <SmallCaps as="div" className={styles.switchHeading}>
              Viewing
            </SmallCaps>
          ),
        },
        {
          title: 'viewing',
          value: 'viewing',
          label: (
            <span className={`${styles.switchOption} ${styles.switchOptionCurrent}`}>
              <span className={styles.switchCheck}>
                <Check size={14} aria-hidden="true" />
              </span>
              <span className={styles.switchTop}>
                <span className={styles.switchName}>{name}</span>
                <span className={styles.flex} />
                <span className={styles.switchCount}>none of your open tasks</span>
              </span>
            </span>
          ),
          onClick: () => {},
        },
      ];
  const options = [...viewing, heading, ...rows];

  if (!rows.length) return <span className={styles.crumbCurrent}>{name}</span>;

  return (
    <Menu
      classNames={[styles.switchMenu]}
      customOptions={options}
      paperWidth={440}
      button={
        <span className={styles.switchTrigger}>
          {name}
          <ChevronDown size={14} aria-hidden="true" />
        </span>
      }
    />
  );
};

MemberOption.propTypes = {
  member: PropTypes.object.isRequired,
  current: PropTypes.bool,
};

MemberSwitcher.propTypes = {
  name: PropTypes.string.isRequired,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  members: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default MemberSwitcher;
